<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">订单管理</h1>
            <p class="list-page-description">管理服务订单，跟踪服务进度</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
    <!-- 页面标题 -->
    <!-- <div class="mb-6">
      <h1 class="text-2xl font-bold text-slate-900">订单管理</h1>
      <p class="text-slate-600 mt-1">管理服务订单，跟踪服务进度</p>
    </div> -->

    <!-- 搜索和筛选 -->
    <PageFilterBar
      body-class="border-b border-slate-200 p-6"
      show-refresh
      :refresh-loading="loading"
      @refresh="loadOrders"
    >
      <div class="flex gap-3 flex-1 flex-wrap items-center">
        <div class="relative flex-1 max-w-sm min-w-[200px]">
              <input
                v-model="searchForm.keyword"
                type="text"
                placeholder="搜索订单编号、产品名称"
                class="form-search-input"
                @keyup.enter="handleSearch"
              >
              <svg class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
        </div>
        <select v-model="searchForm.status" class="form-select-native form-control-sm w-32" @change="handleSearch">
              <option value="">全部状态</option>
              <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
        <select v-model="searchForm.planId" class="form-select-native form-control-sm w-32" @change="handleSearch">
              <option value="">全部方案</option>
              <option v-for="p in planList" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <div class="w-44">
          <el-select
                v-model="searchForm.executorId"
                filterable
                remote
                clearable
                reserve-keyword
                placeholder="选择执行人"
                :remote-method="handleExecutorSearch"
                :loading="executorLoading"
                class="w-full form-select form-control-sm"
                popper-class="executor-select-dropdown"
                @visible-change="handleExecutorDropdownVisible"
                @change="handleSearch"
              >
                <el-option
                  v-for="item in executorList"
                  :key="item.id"
                  :label="item.displayName || item.username"
                  :value="item.id"
                >
                  <div class="flex items-center justify-between">
                    <span>{{ item.displayName || item.username }}</span>
                    <span v-if="item.phone" class="text-xs text-slate-400 ml-2">{{ item.phone }}</span>
                  </div>
                </el-option>
                <template v-if="executorPagination.total > executorList.length" #footer>
                  <div class="text-center py-2 border-t border-slate-200">
                    <button
                      type="button"
                      class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                      :disabled="executorLoading"
                      @click.stop="loadMoreExecutors"
                    >
                      {{ executorLoading ? '加载中...' : `加载更多 (${executorList.length}/${executorPagination.total})` }}
                    </button>
                  </div>
                </template>
              </el-select>
            </div>
          </div>

        <!-- 快捷筛选标签 -->
      <template #searchActions>
        <button
          type="button"
          class="btn-primary"
          :disabled="loading"
          @click="handleSearch"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          搜索
        </button>
        <button
          type="button"
          class="btn-secondary"
          :disabled="loading"
          @click="resetSearch"
        >
          重置
        </button>
      </template>

      <template #extra>
        <div class="border-t border-slate-200 pt-4">
          <div class="flex gap-2 flex-wrap">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="searchForm.status = tab.value; handleSearch()"
            :class="[
              'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
              searchForm.status === tab.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            ]"
          >
            {{ tab.label }}
          </button>
          </div>
        </div>
      </template>
    </PageFilterBar>
    </template>

      <!-- 订单列表 -->
    <OrderListTable
        :orders="orderList"
        :loading="loading"
        :pagination="pagination"
        :approve-popover-order-id="approvePopoverOrderId"
        :reject-popover-order-id="rejectPopoverOrderId"
        :reject-reason="rejectReason"
        :approval-submitting="approvalSubmitting"
        @detail="openDetailModal"
        @plan-click="openPlanDetail"
        @open-approve-popover="openApprovePopover"
        @close-approve-popover="closeApprovePopover"
        @open-reject-popover="openRejectPopover"
        @close-reject-popover="closeRejectPopover"
        @update:reject-reason="handleRejectReasonChange"
        @approve="handleApprove"
        @edit="openEditModal"
        @status-change="handleStatusChange"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />

    <!-- 订单详情弹窗 -->
    <OrderDetailModal
      v-model:visible="showDetailModal"
      :orderDetail="orderDetail"
      :traceList="traceList"
      @receive="openReceiveModal"
      @editTask="openTaskEditModal"
      @edit="handleDetailEdit"
      @approve="handleDetailApprove"
      @refreshDetail="handleDetailRefresh"
      @executorChanged="handleExecutorChanged"
    />

    <!-- 方案详情弹窗 -->
    <PlanDetailModal
      v-model:visible="showPlanModal"
      :planData="selectedPlanData"
    />

    <!-- 订单编辑弹窗 -->
    <OrderFormModal
      v-model:visible="showOrderEditModal"
      :isEdit="true"
      :editData="editOrderData"
      @success="loadOrders"
    />

    <!-- 任务编辑弹窗 -->
    <div v-if="showTaskEditModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeTaskEditModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">编辑任务</h3>
            <button @click="closeTaskEditModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <!-- 只读信息 -->
            <div class="mb-6 p-4 bg-slate-50 rounded-lg space-y-2">
              <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">任务名称</span>
                <span class="text-sm text-slate-900">{{ selectedTask?.name }}</span>
              </div>
              <div v-if="selectedTask?.type" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">任务类型</span>
                <span class="text-sm text-slate-900">{{ selectedTask?.type }}</span>
              </div>
              <div v-if="selectedTask?.assigneeName" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">负责人</span>
                <div class="flex items-center gap-2">
                  <div class="w-6 h-6 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {{ selectedTask?.assigneeName?.charAt(0) }}
                  </div>
                  <span class="text-sm text-slate-900">{{ selectedTask?.assigneeName }}</span>
                </div>
              </div>
              <div v-if="selectedTask?.startTime" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">开始时间</span>
                <span class="text-sm text-slate-900">{{ $formatDateTime(selectedTask?.startTime) }}</span>
              </div>
              <div v-if="selectedTask?.endTime" class="flex items-center gap-3">
                <span class="text-sm font-medium text-slate-500 w-20">结束时间</span>
                <span class="text-sm text-slate-900">{{ $formatDateTime(selectedTask?.endTime) }}</span>
              </div>
            </div>

            <!-- 可编辑表单 -->
            <el-form ref="taskFormRef" :model="taskFormData" :rules="taskRules" label-width="100px">
              <el-form-item label="任务状态" prop="status">
                <el-select v-model="taskFormData.status" placeholder="请选择任务状态" class="w-full">
                  <el-option label="待开始" :value="0" />
                  <el-option label="进行中" :value="1" />
                  <el-option label="已完成" :value="2" />
                  <el-option label="已暂停" :value="3" />
                </el-select>
              </el-form-item>

              <el-form-item label="任务进度" prop="progress">
                <div class="flex items-center gap-3 w-full">
                  <el-slider v-model="taskFormData.progress" :min="0" :max="100" class="flex-1" />
                  <el-input-number
                    v-model="taskFormData.progress"
                    :min="0"
                    :max="100"
                    style="width: 100px"
                  />
                  <span class="text-slate-600">%</span>
                </div>
              </el-form-item>

              <el-form-item label="备注" prop="remark">
                <el-input
                  v-model="taskFormData.remark"
                  type="textarea"
                  :rows="4"
                  placeholder="请输入备注信息"
                />
              </el-form-item>
            </el-form>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeTaskEditModal" class="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">取消</button>
            <button :disabled="taskSubmitting" @click="handleTaskSubmit" class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
              {{ taskSubmitting ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 收款弹窗 -->
    <div v-if="showBillingReceiveModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeReceiveModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-md w-full">
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">账单收款</h3>
            <button @click="closeReceiveModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="p-6">
            <el-form ref="receiveFormRef" :model="receiveFormData" :rules="receiveRules" label-width="100px">
              <el-form-item label="应收金额">
                <div class="text-lg font-medium text-slate-900">¥{{ selectedBilling?.amount?.toLocaleString() }}</div>
              </el-form-item>
              <el-form-item label="已收金额">
                <div class="text-slate-600">¥{{ selectedBilling?.paidAmount?.toLocaleString() || '0' }}</div>
              </el-form-item>
              <el-form-item label="待收金额">
                <div class="text-amber-600 font-medium">¥{{ receiveRemainingAmount.toLocaleString() }}</div>
              </el-form-item>
              <el-form-item label="收款金额" prop="paidAmount">
                <el-input-number
                  v-model="receiveFormData.paidAmount"
                  :min="0.01"
                  :max="receiveRemainingAmount"
                  :precision="2"
                  :step="100"
                  style="width: 200px"
                />
              </el-form-item>
              <el-form-item label="支付渠道" prop="payChannel">
                <el-select v-model="receiveFormData.payChannel" placeholder="请选择支付渠道" style="width: 200px">
                  <el-option v-for="ch in payChannelOptions" :key="ch.value" :label="ch.label" :value="ch.value" />
                </el-select>
              </el-form-item>
            </el-form>
          </div>

          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button @click="closeReceiveModal" class="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">取消</button>
            <button v-permission="'billing:receive'" type="primary" :loading="receiveSubmitting" @click="handleReceiveSubmit" class="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">确认收款</button>
          </div>
        </div>
      </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getOrderPage, getOrderDetail, updateOrderStatus, approveOrder } from '@/api/order'
import { getTraceList } from '@/api/trace'
import { getPlanPage } from '@/api/plan'
import { receiveBilling } from '@/api/billing'
import { getUserList } from '@/api/user'
import { updateTask } from '@/api/task'
import PlanDetailModal from '@/components/PlanDetailModal.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import OrderDetailModal from '@/components/OrderDetailModal.vue'
import OrderFormModal from '@/components/OrderFormModal.vue'
import OrderListTable from '@/components/OrderListTable.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'

const route = useRoute()

const searchForm = reactive({
  keyword: '',
  status: '',
  planId: '',
  executorId: null,
  page: 1,
  size: 20
})

const pagination = reactive({
  current: 1,
  size: 20,
  total: 0,
  pages: 0
})

const statistics = reactive({
  total: 0,
  pending: 0,
  executing: 0,
  completed: 0,
  cancelled: 0
})

const loading = ref(false)
const orderList = ref([])
const planList = ref([])
const orderDetail = ref(null)
const traceList = ref([])
const showDetailModal = ref(false)
const showPlanModal = ref(false)
const selectedPlanData = ref(null)
const selectedBilling = ref(null)
const showBillingReceiveModal = ref(false)
const receiveFormRef = ref(null)
const receiveSubmitting = ref(false)

// 订单编辑相关状态
const showOrderEditModal = ref(false)
const editOrderData = ref(null)

// 审批相关状态
const approvePopoverOrderId = ref(null)
const rejectPopoverOrderId = ref(null)
const rejectReason = ref('')
const approvalSubmitting = ref(false)

// 任务编辑相关状态
const showTaskEditModal = ref(false)
const taskFormRef = ref(null)
const taskSubmitting = ref(false)
const selectedTask = ref(null)
const taskFormData = reactive({
  status: 0,
  progress: 0,
  remark: ''
})

// 执行人相关状态
const executorList = ref([])
const executorLoading = ref(false)
const executorSearchKeyword = ref('')
const executorPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})
const receiveFormData = reactive({
  paidAmount: 0,
  payChannel: ''
})
const receiveRules = {
  paidAmount: [
    { required: true, message: '请输入收款金额', trigger: 'blur' },
    { validator: (rule, value, callback) => {
      if (!value || value <= 0) {
        callback(new Error('收款金额必须大于0'))
      } else {
        callback()
      }
    }, trigger: 'blur' }
  ],
  payChannel: [
    { required: true, message: '请选择支付渠道', trigger: 'change' }
  ]
}

const taskRules = {
  status: [
    { required: true, message: '请选择任务状态', trigger: 'change' }
  ],
  progress: [
    { required: true, message: '请输入任务进度', trigger: 'blur' },
    { type: 'number', min: 0, max: 100, message: '进度必须在0-100之间', trigger: 'blur' }
  ]
}
const payChannelOptions = [
  { label: '微信', value: 'WECHAT' },
  { label: '支付宝', value: 'ALIPAY' },
  { label: '银行转账', value: 'BANK_TRANSFER' },
  { label: '现金', value: 'CASH' },
  { label: '其他', value: 'OTHER' }
]
const receiveRemainingAmount = computed(() => {
  if (!selectedBilling.value) return 0
  return (selectedBilling.value.amount || 0) - (selectedBilling.value.paidAmount || 0)
})

// 快捷筛选标签
const tabs = [
  { label: '全部', value: '' },
  { label: '待确认', value: 0 },
  { label: '执行中', value: 1 },
  { label: '已完成', value: 2 },
  { label: '已取消', value: 3 },
  { label: '已驳回', value: 4 }
]

const statusOptions = [
  { label: '待确认', value: 0 },
  { label: '执行中', value: 1 },
  { label: '已完成', value: 2 },
  { label: '已取消', value: 3 },
  { label: '已驳回', value: 4 }
]

// 执行人相关方法
const fetchExecutors = async (keyword = '', isLoadMore = false) => {
  if (executorLoading.value) return

  executorLoading.value = true
  try {
    const params = {
      page: isLoadMore ? executorPagination.page + 1 : 1,
      size: executorPagination.size,
      status: 0 // 只查询正常状态的用户
    }
    if (keyword) {
      params.username = keyword
    }

    const result = await getUserList(params)

    if (isLoadMore) {
      executorList.value = [...executorList.value, ...(result?.records || [])]
      executorPagination.page = result?.current || executorPagination.page + 1
    } else {
      executorList.value = result?.records || []
      executorPagination.page = result?.current || 1
    }

    executorPagination.total = result?.total || 0
  } catch (error) {
    console.error('获取执行人列表失败:', error)
    ElMessage.error('获取执行人列表失败')
  } finally {
    executorLoading.value = false
  }
}

let executorSearchTimer = null
const handleExecutorSearch = (query) => {
  executorSearchKeyword.value = query
  // 添加防抖，避免频繁请求
  if (executorSearchTimer) {
    clearTimeout(executorSearchTimer)
  }
  executorSearchTimer = setTimeout(() => {
    fetchExecutors(query, false)
  }, 300)
}

const handleExecutorDropdownVisible = (visible) => {
  if (visible && executorList.value.length === 0) {
    fetchExecutors('', false)
  }
}

const loadMoreExecutors = () => {
  if (!executorLoading.value) {
    fetchExecutors(executorSearchKeyword.value, true)
  }
}

const loadOrders = async () => {
  loading.value = true
  try {
    const params = {
      keyword: searchForm.keyword,
      status: searchForm.status !== '' ? searchForm.status : undefined,
      planId: searchForm.planId || undefined,
      executorId: searchForm.executorId || undefined,
      page: pagination.current,
      size: pagination.size
    }
    const data = await getOrderPage(params)
    orderList.value = data.records || []
    pagination.current = data.current
    pagination.size = data.size
    pagination.total = data.total
    pagination.pages = data.pages

    // 更新统计
    statistics.total = data.total
    statistics.pending = data.records?.filter(o => o.status === 0).length || 0
    statistics.executing = data.records?.filter(o => o.status === 1).length || 0
    statistics.completed = data.records?.filter(o => o.status === 2).length || 0
    statistics.cancelled = data.records?.filter(o => o.status === 3).length || 0
  } catch (error) {
    ElMessage.error('加载订单列表失败')
  } finally {
    loading.value = false
  }
}

const loadPlans = async () => {
  try {
    const data = await getPlanPage({ page: 1, size: 100 })
    planList.value = data.records || []
  } catch (error) {
    console.error('加载方案列表失败', error)
  }
}

const handleSearch = () => {
  pagination.current = 1
  loadOrders()
}

const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.status = ''
  searchForm.planId = ''
  searchForm.executorId = null
  pagination.current = 1
  loadOrders()
}

const handlePageChange = (page) => {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  loadOrders()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  loadOrders()
}

const openDetailModal = async (order) => {
  try {
    orderDetail.value = await getOrderDetail(order.id)
    traceList.value = await getTraceList('ORDER', order.id)
  } catch (error) {
    ElMessage.error('加载订单详情失败')
  }
  showDetailModal.value = true
}

const handleDetailRefresh = async () => {
  if (!orderDetail.value?.id) {
    return
  }
  try {
    orderDetail.value = await getOrderDetail(orderDetail.value.id)
    traceList.value = await getTraceList('ORDER', orderDetail.value.id)
    loadOrders()
  } catch (error) {
    ElMessage.error(error?.message || '刷新订单详情失败')
  }
}

// 打开订单编辑弹窗
const openEditModal = async (order) => {
  try {
    editOrderData.value = await getOrderDetail(order.id)
    showOrderEditModal.value = true
  } catch (error) {
    ElMessage.error('加载订单详情失败')
  }
}

// 打开方案详情弹窗
const openPlanDetail = (order) => {
  selectedPlanData.value = null
  if (order.planId) {
    selectedPlanData.value = { id: order.planId }
  }
  showPlanModal.value = true
}

const handleStatusChange = async ({ order, status }) => {
  try {
    await updateOrderStatus({ orderId: order.id, status })
    ElMessage.success('状态更新成功')
    loadOrders()
  } catch (error) {
    ElMessage.error(error.message || '状态更新失败')
  }
}

// 审批相关方法
const openRejectPopover = (order) => {
  closeApprovePopover()
  rejectPopoverOrderId.value = order.id
  rejectReason.value = ''
}

const closeRejectPopover = () => {
  rejectPopoverOrderId.value = null
  rejectReason.value = ''
}

const handleRejectReasonChange = (value) => {
  rejectReason.value = value
}

const openApprovePopover = (order) => {
  closeRejectPopover()
  approvePopoverOrderId.value = order.id
}

const closeApprovePopover = () => {
  approvePopoverOrderId.value = null
}

const handleApprove = async ({ order, approved }) => {
  if (!approved && !rejectReason.value?.trim()) {
    ElMessage.warning('请填写不通过原因')
    return
  }
  approvalSubmitting.value = true
  try {
    await approveOrder({
      orderId: order.id,
      approved,
      reason: approved ? '' : rejectReason.value.trim()
    })
    ElMessage.success(approved ? '已通过' : '已拒绝')
    closeApprovePopover()
    closeRejectPopover()
    loadOrders()
  } catch (error) {
    ElMessage.error(error.message || '审批操作失败')
  } finally {
    approvalSubmitting.value = false
  }
}

// 打开收款弹窗
const openReceiveModal = (billing) => {
  console.log('openReceiveModal called', billing)
  selectedBilling.value = billing
  receiveFormData.paidAmount = receiveRemainingAmount.value
  receiveFormData.payChannel = ''
  showBillingReceiveModal.value = true
}

// 关闭收款弹窗
const closeReceiveModal = () => {
  showBillingReceiveModal.value = false
}

// 提交收款
const handleReceiveSubmit = async () => {
  try {
    await receiveFormRef.value.validate()
  } catch {
    return
  }

  receiveSubmitting.value = true
  try {
    await receiveBilling({
      billingId: selectedBilling.value.id,
      paidAmount: receiveFormData.paidAmount,
      payChannel: receiveFormData.payChannel
    })
    ElMessage.success('收款成功')
    closeReceiveModal()
    // 刷新订单详情
    if (orderDetail.value) {
      orderDetail.value = await getOrderDetail(orderDetail.value.id)
    }
  } catch (error) {
    ElMessage.error(error.message || '收款失败')
  } finally {
    receiveSubmitting.value = false
  }
}

// 收款成功回调
// 已移至 handleReceiveSubmit 中直接处理

// 任务编辑相关方法
const openTaskEditModal = (task) => {
  selectedTask.value = task
  // 只设置可编辑的字段
  taskFormData.status = task.status ?? 0
  taskFormData.progress = task.progress || 0
  taskFormData.remark = task.remark || ''
  showTaskEditModal.value = true
}

const closeTaskEditModal = () => {
  showTaskEditModal.value = false
  if (taskFormRef.value) {
    taskFormRef.value.resetFields()
  }
}

const handleTaskSubmit = async () => {
  try {
    await taskFormRef.value.validate()
  } catch {
    return
  }

  taskSubmitting.value = true
  try {
    // 根据API文档，需要传入 id、orderId 和可修改的字段
    await updateTask({
      id: selectedTask.value.id,
      orderId: selectedTask.value.orderId,
      status: taskFormData.status,
      progress: taskFormData.progress,
      remark: taskFormData.remark
    })

    ElMessage.success('任务更新成功')
    closeTaskEditModal()

    // 刷新订单详情
    if (orderDetail.value) {
      orderDetail.value = await getOrderDetail(orderDetail.value.id)
    }
  } catch (error) {
    ElMessage.error(error.message || '任务更新失败')
  } finally {
    taskSubmitting.value = false
  }
}

// 从详情弹窗编辑订单
const handleDetailEdit = (order) => {
  editOrderData.value = order
  showDetailModal.value = false
  showOrderEditModal.value = true
}

// 从详情弹窗审批订单
const handleDetailApprove = async ({ orderId, approved, reason }) => {
  approvalSubmitting.value = true
  try {
    await approveOrder({ orderId, approved, reason })
    ElMessage.success(approved ? '已通过' : '已驳回')
    showDetailModal.value = false
    loadOrders()
  } catch (error) {
    ElMessage.error(error.message || '审批操作失败')
  } finally {
    approvalSubmitting.value = false
  }
}

// 执行人更换成功回调
const handleExecutorChanged = async () => {
  await handleDetailRefresh()
}

onMounted(async () => {
  // 从路由参数中读取筛选条件
  const { id, orderId, keyword, status, planId, executorId, openDetail } = route.query

  // 如果指定了订单ID且需要打开详情
  const targetOrderId = id || orderId
  if (targetOrderId && openDetail === 'true') {
    try {
      orderDetail.value = await getOrderDetail(targetOrderId)
      traceList.value = await getTraceList('ORDER', targetOrderId)
      showDetailModal.value = true
    } catch (error) {
      console.error('获取订单详情失败:', error)
      ElMessage.error('获取订单详情失败')
    }
    // 清除 query 参数，避免刷新页面重复弹窗
    window.history.replaceState({}, '', '/order')
  } else if (targetOrderId) {
    // 仅筛选，不打开详情
    searchForm.keyword = targetOrderId
  }

  // 关键词搜索
  if (keyword) {
    searchForm.keyword = keyword
  }

  // 状态筛选
  if (status !== undefined && status !== '') {
    searchForm.status = Number(status)
  }

  // 方案筛选
  if (planId) {
    searchForm.planId = planId
  }

  // 执行人筛选
  if (executorId) {
    searchForm.executorId = executorId
  }

  loadOrders()
  loadPlans()
})
</script>
